import { RootState } from '@Core/Communication'
import { UserContainer, UserRegister } from '@User/Data/DataSource/Container'
import { DBUser } from '@User/Domain/Model/User'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

export interface SystemUser {
    /** uid do Firebase — o mesmo id do usuário logado (`user.id`). */
    id: string
    name: string
    mail?: string
}

// Uma busca por sessão: a lista de usuários é usada no filtro, no detalhe e
// na criação, e muda raramente.
let cache: Promise<SystemUser[]> | null = null

const toSystemUser = (user: DBUser & { firebase?: { uid?: string; disabled?: boolean } }): SystemUser | null => {
    const id = user.firebase?.uid ?? user.uid
    if (!id || user.firebase?.disabled || user.disabled) return null
    return { id, name: user.name || user.displayName || user.mail, mail: user.mail }
}

const loadUsers = () => {
    if (!cache) {
        const useCase = UserContainer.get(UserRegister.UseCaseGetUsers)
        cache = useCase
            .execute()
            .then((users) =>
                (users ?? [])
                    .map((user) => toSystemUser(user as any))
                    .filter((user): user is SystemUser => Boolean(user))
                    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
            )
            .catch((error) => {
                cache = null
                throw error
            })
    }
    return cache
}

/** Responsável gravado no processo (registros novos têm uid; antigos só mid/mail). */
export interface AssigneeRef {
    uid?: string
    mid?: string
    name?: string
    mail?: string
}

export const hasAssignee = (ref?: AssigneeRef | null) => Boolean(ref && (ref.uid || ref.mid || ref.name || ref.mail))

/** Nome para exibir: o da lista de usuários; senão o gravado; senão o e-mail. */
export const assigneeDisplayName = (ref: AssigneeRef | null | undefined, users: SystemUser[]): string | undefined => {
    if (!hasAssignee(ref)) return undefined
    const match = users.find(
        (user) => user.id === ref?.uid || user.id === ref?.mid || (!!user.mail && (user.mail === ref?.mail || user.mail === ref?.mid))
    )
    return match?.name || ref?.name || ref?.mail || ref?.mid || 'Responsável'
}

/** Só para testes. */
export const resetSystemUsersCache = () => {
    cache = null
}

export const useSystemUsers = () => {
    const currentUserId = useSelector((state: RootState) => (state.user?.user as any)?.id as string | undefined)
    const [users, setUsers] = useState<SystemUser[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        let active = true
        loadUsers()
            .then((result) => { if (active) setUsers(result) })
            .catch((e) => {
                console.error(e)
                if (active) setError('Não foi possível carregar os usuários')
            })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [])

    return { users, loading, error, currentUserId }
}
